// src/Components/PortfolioGallery.js
import { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

const portfolioItems = [
    {
        id: 1,
        category: 'Brand Identity',
        title: 'Tech Startup Rebrand',
        description: 'Complete brand identity redesign for a cutting-edge technology company',
        imageUrl: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?q=80&w=2000&auto=format&fit=crop',
        tags: ['Logo Design', 'Brand Guidelines', 'Color Palette'],
    },
    {
        id: 2,
        category: 'Web Design',
        title: 'E-Commerce Platform',
        description: 'Modern, user-friendly interface design for online shopping experience',
        imageUrl: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=2000&auto=format&fit=crop',
        tags: ['UI/UX', 'Responsive Design', 'Prototyping'],
    },
    {
        id: 3,
        category: 'Print Design',
        title: 'Annual Report Design',
        description: 'Professional annual report with infographics and data visualization',
        imageUrl: 'https://images.unsplash.com/photo-1586281380349-632531db7ed4?q=80&w=2000&auto=format&fit=crop',
        tags: ['Layout Design', 'Typography', 'Infographics'],
    },
    {
        id: 4,
        category: 'Social Media',
        title: 'Instagram Campaign',
        description: 'Engaging social media campaign for wellness brand',
        imageUrl: 'https://images.unsplash.com/photo-1611162616305-c69b3fa7fbe0?q=80&w=2000&auto=format&fit=crop',
        tags: ['Social Graphics', 'Content Creation', 'Campaign Design'],
    },
    {
        id: 5,
        category: 'Brand Identity',
        title: 'Restaurant Branding',
        description: 'Complete visual identity for upscale dining establishment',
        imageUrl: 'https://images.unsplash.com/photo-1524634126442-357e0eac3c14?q=80&w=2000&auto=format&fit=crop',
        tags: ['Logo Design', 'Menu Design', 'Signage'],
    },
    {
        id: 6,
        category: 'Web Design',
        title: 'Healthcare Portal',
        description: 'Clean, accessible patient portal interface design',
        imageUrl: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?q=80&w=2000&auto=format&fit=crop',
        tags: ['UI Design', 'Accessibility', 'User Research'],
    },
];

const categories = ['All', 'Brand Identity', 'Web Design', 'Print Design', 'Social Media'];

export default function PortfolioGallery() {
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [selectedItem, setSelectedItem] = useState(null);

    const filteredItems = selectedCategory === 'All'
        ? portfolioItems
        : portfolioItems.filter(item => item.category === selectedCategory);

    return (
        <div className="bg-white py-24 sm:py-32">
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
                <div className="mx-auto max-w-2xl text-center">
                    <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">Design Portfolio</h2>
                    <p className="mt-6 text-lg leading-8 text-gray-600">
                        Explore our creative work across various design disciplines. Each project represents our commitment to excellence and innovation.
                    </p>
                </div>

                {/* Category Filter */}
                <div className="mt-12 flex flex-wrap justify-center gap-2">
                    {categories.map((category) => (
                        <button
                            key={category}
                            onClick={() => setSelectedCategory(category)}
                            className={`px-6 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${selectedCategory === category
                                    ? 'bg-indigo-600 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                        >
                            {category}
                        </button>
                    ))}
                </div>

                {/* Portfolio Grid */}
                <div className="mt-16 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
                    {filteredItems.map((item) => (
                        <div
                            key={item.id}
                            className="group relative overflow-hidden rounded-2xl bg-gray-100 cursor-pointer"
                            onClick={() => setSelectedItem(item)}
                        >
                            <div className="aspect-w-16 aspect-h-12">
                                <img
                                    src={item.imageUrl}
                                    alt={item.title}
                                    className="h-64 w-full object-cover transition-transform duration-300 group-hover:scale-110"
                                />
                            </div>
                            <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                                    <p className="text-sm font-medium text-indigo-300">{item.category}</p>
                                    <h3 className="mt-2 text-xl font-bold">{item.title}</h3>
                                    <p className="mt-2 text-sm text-gray-300">{item.description}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Modal */}
                <Dialog open={selectedItem !== null} onClose={() => setSelectedItem(null)} className="relative z-50">
                    <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                    <div className="fixed inset-0 flex items-center justify-center p-4">
                        <Dialog.Panel className="mx-auto max-w-4xl rounded-2xl bg-white p-8">
                            {selectedItem && (
                                <div>
                                    <div className="flex justify-between items-start mb-4">
                                        <div>
                                            <Dialog.Title className="text-2xl font-bold text-gray-900">
                                                {selectedItem.title}
                                            </Dialog.Title>
                                            <p className="mt-1 text-sm text-indigo-600">{selectedItem.category}</p>
                                        </div>
                                        <button
                                            onClick={() => setSelectedItem(null)}
                                            className="rounded-md bg-white text-gray-400 hover:text-gray-500"
                                        >
                                            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                        </button>
                                    </div>
                                    <img
                                        src={selectedItem.imageUrl}
                                        alt={selectedItem.title}
                                        className="w-full rounded-lg"
                                    />
                                    <p className="mt-4 text-gray-600">{selectedItem.description}</p>
                                    <div className="mt-4 flex flex-wrap gap-2">
                                        {selectedItem.tags.map((tag) => (
                                            <span
                                                key={tag}
                                                className="inline-flex items-center rounded-full bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700"
                                            >
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </Dialog.Panel>
                    </div>
                </Dialog>
            </div>
        </div>
    );
}